// ownshipWindsStore.ts — Live winds-aloft profile near ownship.
//
// Independent of the flight-briefing winds fetch. Refreshes when:
//   - Ownship moves > REFRESH_DIST_NM from the last sampled point, or
//   - Last fetch is > REFRESH_AGE_MS old.
//
// Used primarily by the glide-ring layer: every bearing of the ring
// needs the wind at the ownship's *current* altitude (and as the
// aircraft descends through the glide, the wind at the lower altitude
// bands as well — `interpolateAtAlt(hour, altFt)` from wxDecode.ts).
//
// When no profile is available (offline, fetch failed, no data over
// ocean, etc.) the consumer should gracefully fall back to "no-wind"
// glide-ring math.

import { create } from 'zustand';
import { fetchProfile, type WindsProfile } from '../lib/winds';
import { greatCircleNm } from '../lib/geo';

const REFRESH_DIST_NM = 30;
const REFRESH_AGE_MS = 30 * 60 * 1000; // 30 min

interface OwnshipWindsState {
  profile: WindsProfile | null;
  /** Lat/lon where the current profile was sampled. */
  sampledAt: { lat: number; lon: number; time: number } | null;
  /** Set to true while a fetch is in flight. */
  loading: boolean;
  lastError: string | null;
  /**
   * Update the store given the current ownship position. Re-fetches
   * iff distance from last sample > REFRESH_DIST_NM or last sample
   * older than REFRESH_AGE_MS. No-op when ownship lat/lon is null.
   */
  maybeRefresh: (lat: number, lon: number) => void;
}


export const useOwnshipWindsStore = create<OwnshipWindsState>((set, get) => ({
  profile: null,
  sampledAt: null,
  loading: false,
  lastError: null,

  maybeRefresh: (lat, lon) => {
    const now = Date.now();
    const { sampledAt, loading } = get();
    if (loading) return;
    if (sampledAt) {
      const dist = greatCircleNm(sampledAt.lat, sampledAt.lon, lat, lon);
      const age = now - sampledAt.time;
      if (dist < REFRESH_DIST_NM && age < REFRESH_AGE_MS) return;
    }
    set({ loading: true, lastError: null });
    // hours=1: we only need the current forecast hour for the live glide ring.
    fetchProfile(lat, lon, undefined, 1)
      .then((p) => {
        if (!p) {
          set({ loading: false, lastError: 'no winds profile available' });
          return;
        }
        set({
          profile: p,
          sampledAt: { lat, lon, time: Date.now() },
          loading: false,
          lastError: null,
        });
      })
      .catch((e: unknown) => {
        set({
          loading: false,
          lastError: e instanceof Error ? e.message : 'winds fetch failed',
        });
      });
  },
}));
